'use client';

import { defaultLanguage } from '@stereoagency/constants';
import {
  ArticleHero,
  ThumbnailListEntriesSection
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import flush from 'just-flush';
import { ReactNode, useContext } from 'react';
import { articleQuery } from './articleQuery';
import { createDocumentURL, dateFormatterFactory, findInKeyArray, sanityImageAsProps } from '@/utils';
import { SimpleBlockContent } from '@/components/sanity/client';
import { SpecialPagesContext } from '@/components/context';

type Props = {
  lang: string;
  article: (typeof articleQuery)[1];
  content?: ReactNode;
  taxonomyCounts?: Record<string, number>;
};

export default function ArticlePage({
  lang,
  article,
  content,
  taxonomyCounts
}: Props) {
  const { t } = useTranslation('news');
  const { pages } = useContext(SpecialPagesContext);

  const dateFormatter = dateFormatterFactory(lang, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const title = article.title
    ? findInKeyArray(article.title, [lang, defaultLanguage.id])
    : null;

  return (
    <>
      <ArticleHero
        label={t('news:label')}
        alignment="center"
        title={title?.value ?? ''}
        sublabel={
          article.datetime
            ? dateFormatter.format(new Date(article.datetime))
            : undefined
        }
        actions={flush(
          article.taxonomies?.map((tax) => {
            const name = tax.name
              ? findInKeyArray(tax.name, [lang, defaultLanguage.id])
              : null;

            if (
              !name?.value ||
              !tax.slug?.current ||
              !pages.news_listing_page?.href
            ) {
              return null;
            }

            const url = `${pages.news_listing_page.href}?filters=${encodeURIComponent(tax.slug.current)}`;
            const count = taxonomyCounts?.[tax.slug.current] ?? 0;

            return {
              label: name.value,
              href: url,
              suffix: `(${count})`
            };
          }) ?? []
        )}
        accentColor="--colors-green-700"
      />

      {content}

      {!!article.relatedArticles?.length && (
        <ThumbnailListEntriesSection
          title={t('news:related-news.title')}
          action={
            pages.news_listing_page
              ? {
                  href: pages.news_listing_page.href,
                  label: t('news:related-news.action.label')
                }
              : undefined
          }
          entries={flush(
            article.relatedArticles.map((related) => {
              const relatedTitle = related.title
                ? findInKeyArray(related.title, [lang, defaultLanguage.id])
                : null;
              const intro = related.introduction
                ? findInKeyArray(related.introduction, [
                    lang,
                    defaultLanguage.id
                  ])
                : null;

              if (
                !related.slug?.current ||
                !relatedTitle?.value ||
                !related.thumbnail?.asset._ref
              ) {
                return null;
              }

              return {
                title: relatedTitle.value,
                description: intro?.value ? (
                  <SimpleBlockContent value={intro.value} />
                ) : null,
                image: sanityImageAsProps(related.thumbnail),
                action: {
                  href: createDocumentURL(lang, 'article', related.slug.current)
                }
              };
            })
          )}
        />
      )}
    </>
  );
}
